import { useEffect, useState, type ReactNode } from 'react';
import { api, assetUrls } from '../lib/api.js';
import {
  NEUTRAL_EDIT,
  EDIT_RANGES,
  editToCssFilter,
  isNeutral,
  type RawEdit,
} from '../lib/raw-edit.js';

/**
 * Grading a still, without touching the original.
 *
 * The sliders act on the developed JPEG through a CSS filter, so they respond
 * instantly. That is an approximation and is meant to be -- exposure recovered
 * from a JPEG cannot bring back highlights the JPEG never contained. Saving
 * renders properly from the sensor data on the server, which is where the
 * latitude actually is, and where a raw file earns its size.
 *
 * The comparison is the part that makes this safe to use. An edit you cannot
 * hold against the original is a change you are taking on trust.
 */

const CONTROLS: Array<{ key: keyof RawEdit; label: string; step: number }> = [
  { key: 'exposure', label: 'Exposure', step: 0.05 },
  { key: 'contrast', label: 'Contrast', step: 1 },
  { key: 'highlights', label: 'Highlights', step: 1 },
  { key: 'shadows', label: 'Shadows', step: 1 },
  { key: 'whites', label: 'Whites', step: 1 },
  { key: 'blacks', label: 'Blacks', step: 1 },
  { key: 'vibrance', label: 'Vibrance', step: 1 },
  { key: 'saturation', label: 'Saturation', step: 1 },
];

export function RawEditor({
  assetId,
  filename,
  initialEdit,
  hasRenderedEdit,
  onChanged,
}: {
  assetId: string;
  filename: string;
  initialEdit: RawEdit | null;
  hasRenderedEdit: boolean;
  onChanged: () => void;
}): ReactNode {
  const [edit, setEdit] = useState<RawEdit>(initialEdit ?? NEUTRAL_EDIT);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showOriginal, setShowOriginal] = useState(false);
  // Bumped after a save so the browser fetches the new rendition rather than
  // the one it already has under the same URL.
  const [renderedAt, setRenderedAt] = useState(0);

  useEffect(() => {
    setEdit(initialEdit ?? NEUTRAL_EDIT);
  }, [initialEdit]);

  const set = (key: keyof RawEdit, value: number): void => {
    setEdit((prev) => ({ ...prev, [key]: value }));
    setNotice(null);
  };

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await api.saveEdit(assetId, edit);
      setNotice(
        isNeutral(edit)
          ? 'Reset to the original.'
          : 'Saved. Rendering from the raw file — the comparison updates when it finishes.',
      );
      setRenderedAt(Date.now());
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the edit');
    } finally {
      setSaving(false);
    }
  };

  const revert = async (): Promise<void> => {
    if (!confirm('Discard this edit? The original was never modified.')) return;
    setSaving(true);
    setError(null);
    try {
      await api.clearEdit(assetId);
      setEdit(NEUTRAL_EDIT);
      setNotice('Edit removed.');
      setRenderedAt(Date.now());
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not revert');
    } finally {
      setSaving(false);
    }
  };

  // The rendered result once it exists, and the live approximation until then.
  const showRendered = hasRenderedEdit && !showOriginal;
  const source = showRendered
    ? `${assetUrls.edited(assetId)}?v=${renderedAt}`
    : assetUrls.proxy(assetId);

  return (
    <div className="panel" style={{ marginTop: 14 }}>
      <div className="flex" style={{ alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>Develop</h3>
        <div className="spacer" />
        <label className="flex" style={{ alignItems: 'center', gap: 6, fontSize: 12 }}>
          <input
            type="checkbox"
            checked={showOriginal}
            onChange={(e) => setShowOriginal(e.target.checked)}
          />
          Show original
        </label>
      </div>

      {error && <div className="notice error">{error}</div>}
      {notice && <div className="notice success">{notice}</div>}

      <div className="viewer" style={{ marginTop: 10 }}>
        <img
          src={source}
          alt={filename}
          // Only the live approximation is filtered. Filtering the rendered
          // result would apply the grade twice.
          style={{ filter: showOriginal || showRendered ? 'none' : editToCssFilter(edit) }}
        />
      </div>

      <p className="muted" style={{ fontSize: 11, marginTop: 6 }}>
        {showOriginal
          ? 'The original, as it came off the camera.'
          : showRendered
            ? 'Rendered from the raw file.'
            : 'Live preview — an approximation. Save to render from the sensor data.'}
      </p>

      <div style={{ marginTop: 10 }}>
        {CONTROLS.map(({ key, label, step }) => (
          <div key={key} className="flex" style={{ alignItems: 'center', gap: 10 }}>
            <label style={{ width: 90, fontSize: 12 }} htmlFor={`edit-${key}`}>
              {label}
            </label>
            <input
              id={`edit-${key}`}
              type="range"
              min={EDIT_RANGES[key].min}
              max={EDIT_RANGES[key].max}
              step={step}
              value={edit[key]}
              onChange={(e) => set(key, Number(e.target.value))}
              disabled={saving}
              style={{ flex: 1 }}
            />
            <span style={{ width: 52, textAlign: 'right', fontSize: 12, color: 'var(--text-dim)' }}>
              {key === 'exposure' ? edit[key].toFixed(2) : Math.round(edit[key])}
            </span>
          </div>
        ))}

        <div className="flex" style={{ alignItems: 'center', gap: 10, marginTop: 4 }}>
          <label style={{ width: 90, fontSize: 12 }} htmlFor="edit-temperature">
            Temperature
          </label>
          <input
            id="edit-temperature"
            type="range"
            min={2000}
            max={12000}
            step={50}
            // Zero means "as shot", which is not a point on this scale, so the
            // slider starts at daylight until it is deliberately moved.
            value={edit.temperature || 5500}
            onChange={(e) => set('temperature', Number(e.target.value))}
            disabled={saving}
            style={{ flex: 1 }}
          />
          <span style={{ width: 52, textAlign: 'right', fontSize: 12, color: 'var(--text-dim)' }}>
            {edit.temperature ? `${Math.round(edit.temperature)}K` : 'as shot'}
          </span>
        </div>
      </div>

      <div className="flex" style={{ marginTop: 12 }}>
        <button className="btn primary" onClick={() => void save()} disabled={saving}>
          {saving ? 'Saving…' : 'Save and render'}
        </button>
        <button className="btn" onClick={() => setEdit(NEUTRAL_EDIT)} disabled={saving}>
          Reset sliders
        </button>
        {hasRenderedEdit && (
          <button className="btn danger" onClick={() => void revert()} disabled={saving}>
            Remove edit
          </button>
        )}
        <div className="spacer" />
        {hasRenderedEdit && (
          <a
            className="btn sm"
            href={assetUrls.editXmp(assetId)}
            title="Camera Raw settings, readable by Lightroom and darktable"
          >
            Download .xmp
          </a>
        )}
      </div>
    </div>
  );
}
